import { calculateItemTotal } from "@sellar-io/common";
import {
  BasketContextActionEnum,
  useBasketContext,
} from "../../../../../hooks";
import { useStorefrontPromotion } from "./StorefrontPromotionProvider.shared";
import db from "../../../../../db";
import { isPromotionalOrderItem } from "./isPromotionalOrderItem";
import { getPercentageSpendRequirementMet } from "./getPercentageSpendRequirementMet";

export const useStorefrontPromotionRewardItemSelectorParameters = () => {
  const [{ order: basketOrder }, dispatchBasket] = useBasketContext();
  const {
    promotions,
    isFetchingPromotions,
    claimedPromotionRewardItemIds,
    claimPromotionRewardItem,
    unclaimPromotionRewardItem,
    applyEligiblePromotionRewards,
  } = useStorefrontPromotion();

  const handleApplyPromotionsToBasket = (
    claimedPromotionRewardItemIds: number[],
  ) => {
    let newBasketOrder = basketOrder
      ? applyEligiblePromotionRewards({
          order: basketOrder,
          claimedPromotionRewardItemIds,
        })
      : basketOrder;

    const updatedOrder = {
      ...newBasketOrder,
      RetailerId: newBasketOrder?.RetailerId ?? 0,
    };

    dispatchBasket({
      type: BasketContextActionEnum.CHANGE_ORDER,
      order: updatedOrder,
    });

    // This allows us to persist changes between reloads
    db.draftOrders.put(updatedOrder);
  };

  const basketOrderItems = basketOrder?.OrderItems ?? [];

  const nonPromotionBasketOrderItems = basketOrderItems.filter(
    (basketOrderItem) => !isPromotionalOrderItem(basketOrderItem),
  );

  const promotionsWithRewardsWhereSpendThresholdMet = promotions
    .map((promotion) => {
      const updatedPromotionRewardItems = promotion.PromotionRewardItems.filter(
        (promotionRewardItem) =>
          getPercentageSpendRequirementMet({
            spendThreshold: promotionRewardItem.spendThreshold,
            basketSubtotal: calculateItemTotal(basketOrderItems),
          }) === 100,
      );

      return {
        ...promotion,
        PromotionRewardItems: updatedPromotionRewardItems,
      };
    })
    .filter((promotion) => promotion.PromotionRewardItems.length > 0);

  const rewardsWithSpendThresholdMet =
    promotionsWithRewardsWhereSpendThresholdMet.flatMap(
      (promotion) => promotion.PromotionRewardItems,
    );

  const hasPromotionalOrderItemsInBasket = basketOrderItems.some(
    (basketOrderItem) => isPromotionalOrderItem(basketOrderItem),
  );

  const hasEligibleRewardsAvailable =
    rewardsWithSpendThresholdMet.length > 0 &&
    !hasPromotionalOrderItemsInBasket &&
    promotions.length > 0;

  return {
    hasEligibleRewardsAvailable,
    rewardsWithSpendThresholdMet,
    promotions,
    nonPromotionBasketOrderItems,
    promotionsWithRewardsWhereSpendThresholdMet,
    claimedPromotionRewardItemIds,
    isFetchingPromotions,
    handleApplyPromotionsToBasket,
    claimPromotionRewardItem,
    unclaimPromotionRewardItem,
  };
};
